'use client';

import { alpha, Box, Paper, Portal } from '@mui/material';

import { useSearchDropdown } from '@/hooks';
import { SearchDropdownContent } from './SearchDropdownContent';

interface SearchDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  searchQuery?: string;
}

export const SearchDropdown = ({
  isOpen,
  onClose,
  anchorRef,
  searchQuery = '',
}: SearchDropdownProps) => {
  const { state, ref, handlers } = useSearchDropdown({ isOpen, onClose, anchorRef, searchQuery });
  const {
    position,
    recentSearches,
    searchResults,
    isLoading,
    hasAnyResults,
    showResults,
    activeTab,
  } = state;

  const { dropdownRef } = ref;

  const {
    setActiveTab,
    handleResultClick,
    handleProfileClick,
    handleBrowseClick,
    handleSeeAllClick,
    removeFromRecent,
    clearAllRecent,
  } = handlers;

  if (!isOpen) return null;

  return (
    <Portal>
      <Paper
        ref={dropdownRef}
        elevation={0}
        sx={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: position.width,
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflow: 'hidden',
          borderRadius: '12px',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.common.white,
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: (theme) =>
            theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? `0 20px 25px -5px ${alpha(theme.palette.common.black, 0.7)}, 0 10px 10px -5px ${alpha(theme.palette.common.black, 0.5)}`
              : `0 20px 25px -5px ${alpha(theme.palette.common.black, 0.1)}, 0 10px 10px -5px ${alpha(theme.palette.common.black, 0.04)}`,
        }}
      >
        <Box
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            p: 0,
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: (theme) => alpha(theme.palette.divider, 0.1),
              borderRadius: '10px',
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.divider, 0.2) },
            },
          }}
        >
          <SearchDropdownContent
            showResults={showResults}
            isLoading={isLoading}
            hasAnyResults={hasAnyResults}
            searchQuery={searchQuery}
            recentSearches={recentSearches}
            searchResults={searchResults}
            handleBrowseClick={handleBrowseClick}
            handleResultClick={handleResultClick}
            removeFromRecent={removeFromRecent}
            handleProfileClick={handleProfileClick}
            handleSeeAllClick={handleSeeAllClick}
            clearAllRecent={clearAllRecent}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </Box>
      </Paper>
    </Portal>
  );
};
